import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MessageSquare, Star, HelpCircle, Send, ExternalLink, ChevronRight, Bell, User, Shield } from 'lucide-react';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import { useFeedback } from '@contexts/FeedbackContext';

const Settings = () => {
  const navigate = useNavigate();
  const { forceShowFeedbackModal } = useFeedback();
  const [activeTab, setActiveTab] = useState('preferences');

  const handleGiveFeedback = () => {
    forceShowFeedbackModal();
  };

  const handleRateUs = () => {
    forceShowFeedbackModal();
  };

  const handleGoToSupport = () => {
    navigate('/support');
  };

  const tabs = [
    { id: 'preferences', label: 'Preferences', icon: Bell },
    { id: 'feedback', label: 'Feedback', icon: Star },
    { id: 'support', label: 'Support', icon: HelpCircle },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Tabs */}
      <div className="bg-white border-b border-gray-200 px-4">
        <div className="flex gap-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all ${
                  activeTab === tab.id
                    ? 'border-primary-600 text-primary-600 font-semibold'
                    : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto p-4">
          {activeTab === 'preferences' && (
            <div>
              <Card className="p-4">
                <h3 className="text-base font-bold text-gray-900 mb-3">Notifications</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">Email notifications</p>
                      <p className="text-xs text-gray-600">Receive updates and alerts via email</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">Push notifications</p>
                      <p className="text-xs text-gray-600">Get instant notifications on your device</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" defaultChecked className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <p className="font-medium text-gray-900 text-sm">Weekly digest</p>
                      <p className="text-xs text-gray-600">Summary of your weekly activity</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>
              </Card>
            </div>
          )}

          {activeTab === 'feedback' && (
            <div className="space-y-3">
              <Card className="p-4">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                  <div className="flex items-start gap-3">
                    <MessageSquare className="w-5 h-5 text-blue-600 mt-1 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900 mb-1">Share Your Thoughts</h3>
                      <p className="text-sm text-gray-600 mb-3">
                        Your feedback helps us build better features and improve your experience.
                      </p>
                      <Button
                        onClick={handleGiveFeedback}
                        variant="primary"
                        size="md"
                        className="gap-2"
                      >
                        <Star className="w-4 h-4" />
                        Give Feedback
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <button
                  onClick={handleRateUs}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200 hover:bg-gray-100 transition-colors cursor-pointer"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-yellow-100 rounded-lg flex items-center justify-center">
                      <Star className="w-4 h-4 text-yellow-600" />
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-900 text-sm">Rate us 5 stars</p>
                      <p className="text-xs text-gray-600">Help others discover F.O.W</p>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
              </Card>
            </div>
          )}

          {activeTab === 'support' && (
            <div className="space-y-3">
              <Card className="p-4">
                <button
                  onClick={handleGoToSupport}
                  className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-100 hover:border-green-200 transition-all group"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-600 rounded-lg flex items-center justify-center">
                      <MessageSquare className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="font-semibold text-gray-900">Support Center</p>
                      <p className="text-sm text-gray-600">Create and manage support tickets</p>
                    </div>
                  </div>
                  <ExternalLink className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                </button>
              </Card>

              <Card className="p-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center">
                        <HelpCircle className="w-4 h-4 text-purple-600" />
                      </div>
                      <p className="font-medium text-gray-900 text-sm">FAQ</p>
                    </div>
                    <p className="text-xs text-gray-600">Common questions</p>
                  </div>

                  <div className="p-3 bg-white rounded-lg border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                        <Send className="w-4 h-4 text-orange-600" />
                      </div>
                      <p className="font-medium text-gray-900 text-sm">Contact Us</p>
                    </div>
                    <p className="text-xs text-gray-600">Reach out directly</p>
                  </div>
                </div>
              </Card>

              <Card className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                    <span className="text-white text-xs font-bold">?</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900 mb-1">Need immediate help?</p>
                    <p className="text-xs text-gray-600 mb-2">
                      Our support team typically responds within 24 hours.
                    </p>
                    <button
                      onClick={handleGoToSupport}
                      className="text-xs font-medium text-blue-600 hover:text-blue-700 inline-flex items-center gap-1"
                    >
                      Create Support Ticket
                      <ChevronRight className="w-3 h-3" />
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
